import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Lock, ArrowLeft, Check, X } from "lucide-react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import MetricsPanel from "@/components/MetricsPanel";
import { Reveal } from "@/components/Reveal";

function List({ title, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">{title}</h3>
      <ul className="mt-4 space-y-2.5">
        {items.map((it, i) => (
          <li key={i} className="flex gap-3 font-sans text-sm leading-relaxed text-softgrey">
            <Check size={15} className="mt-0.5 shrink-0 text-gold/70" /> <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function OpportunityDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [opp, setOpp] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/opportunities/${slug}`).then((r) => setOpp(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/enquiries", { ...form, opportunity_id: opp.id, opportunity_title: opp.title });
      toast.success(data.message);
      setModal(false);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (notFound) return <div className="flex min-h-screen items-center justify-center bg-obsidian font-serif text-2xl text-gold">Opportunity not found.</div>;
  if (!opp) return <div className="flex min-h-screen items-center justify-center bg-obsidian font-serif text-gold">Loading…</div>;

  const img = opp.images?.[0] ? fileUrl(opp.images[0]) : null;

  return (
    <div data-testid="opportunity-detail" className="min-h-screen bg-obsidian pt-24">
      {/* hero image */}
      <div className="relative h-[46vh] min-h-[340px] w-full overflow-hidden">
        {img ? <img src={img} alt={opp.title} className="h-full w-full object-cover opacity-60" /> : <div className="h-full w-full bg-seablue/40" />}
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/50 to-obsidian/20" />
        <div className="absolute inset-0 mx-auto flex max-w-7xl flex-col justify-end px-6 pb-10 lg:px-10">
          <button onClick={() => navigate("/opportunities")} className="mb-6 flex w-fit items-center gap-2 font-sans text-xs text-softgrey hover:text-warmwhite" data-testid="back-to-opportunities">
            <ArrowLeft size={14} /> All Opportunities
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-1.5 border border-gold/40 bg-obsidian/80 px-3 py-1 backdrop-blur-sm">
              <Lock size={11} className="text-gold" />
              <span className="font-sans text-[10px] tracking-luxe uppercase text-champagne">{opp.badge}</span>
            </span>
            <span className="font-mono text-[11px] text-gold">DEAL ID: {opp.deal_id}</span>
            <span className="font-sans text-[10px] tracking-luxe uppercase text-softgrey">{opp.category}</span>
          </div>
          <h1 className="mt-4 max-w-4xl font-serif text-3xl leading-tight text-warmwhite lg:text-5xl">{opp.title}</h1>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 lg:grid-cols-[1.6fr_1fr] lg:px-10">
        <div className="space-y-14">
          {/* key facts */}
          <div className="grid grid-cols-2 gap-6 border-b border-gold/10 pb-10 md:grid-cols-4">
            {[
              { l: "Location", v: opp.location, mono: false },
              { l: "Asset Type", v: opp.asset_type, mono: false },
              { l: "Starting From", v: opp.starting_price, mono: true },
              { l: "Sourcing / Founder's Fee", v: opp.founders_fee, mono: true },
            ].filter((f) => f.v).map((f, i) => (
              <div key={i}>
                <div className="font-sans text-[9px] tracking-luxe uppercase text-softgrey/60">{f.l}</div>
                <div className={`mt-2 text-sm text-warmwhite ${f.mono ? "font-mono text-champagne" : "font-sans"}`}>{f.v}</div>
              </div>
            ))}
          </div>

          {opp.metrics?.show && (
            <div>
              <h3 className="mb-5 font-sans text-[11px] tracking-luxe uppercase text-gold">Investor Deal Metrics</h3>
              <MetricsPanel metrics={opp.metrics} disclaimer={opp.disclaimer} />
            </div>
          )}

          <List title="Property Overview" items={opp.overview} />
          <List title="Location & Market Advantage" items={opp.location_advantage} />

          {(opp.snapshot_from || opp.snapshot_items?.length) && (
            <div>
              <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">Investment / Commercial Snapshot</h3>
              {opp.snapshot_from && <div className="mt-4 font-mono text-2xl text-warmwhite">Starting from {opp.snapshot_from}</div>}
              <List title="" items={opp.snapshot_items} />
            </div>
          )}

          <List title="Payment Structure" items={opp.payment_structure} />
          <List title="Investor Highlights" items={opp.investor_highlights} />
          <List title="Lifestyle & Amenities" items={opp.amenities} />

          {opp.disclaimer && (
            <p className="border-t border-gold/10 pt-8 font-sans text-xs italic leading-relaxed text-softgrey/60">{opp.disclaimer}</p>
          )}
        </div>

        {/* sticky CTA */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <div className="border border-gold/20 bg-seablue/20 p-8">
            {opp.value_proposition && <p className="font-serif text-lg italic leading-relaxed text-warmwhite">"{opp.value_proposition}"</p>}
            <div className="gold-hairline my-6" />
            {opp.founders_fee && (
              <div className="mb-6">
                <div className="font-sans text-[9px] tracking-luxe uppercase text-softgrey/60">Sourcing / Founder's Fee</div>
                <div className="mt-1 font-mono text-xl text-champagne">{opp.founders_fee}</div>
                <div className="mt-1 font-sans text-[10px] uppercase tracking-wide text-softgrey/50">{opp.fee_type} fee</div>
              </div>
            )}
            <button onClick={() => setModal(true)} data-testid="access-opportunity-btn"
                    className="w-full bg-gold py-4 font-sans text-sm font-medium tracking-wide text-obsidian transition-all hover:brightness-110">
              Access This Opportunity
            </button>
            {opp.confidential && (
              <p className="mt-5 font-sans text-xs leading-relaxed text-softgrey/70">
                Full opportunity particulars are released following the applicable engagement / sourcing arrangement.
              </p>
            )}
          </div>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-obsidian/80 p-4 backdrop-blur-sm" data-testid="enquiry-modal">
          <div className="relative w-full max-w-md border border-gold/25 bg-obsidian p-8">
            <button onClick={() => setModal(false)} className="absolute right-5 top-5 text-softgrey hover:text-warmwhite"><X size={20} /></button>
            <h3 className="font-serif text-2xl text-warmwhite">Access This Opportunity</h3>
            <p className="mt-2 font-sans text-xs text-softgrey">Register your interest — we'll respond discreetly regarding disclosure.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              {[
                { k: "name", p: "Full Name", t: "text", req: true },
                { k: "email", p: "Email", t: "email", req: true },
                { k: "phone", p: "Phone / WhatsApp", t: "text", req: false },
              ].map((f) => (
                <input key={f.k} type={f.t} required={f.req} placeholder={f.p} value={form[f.k]}
                       onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                       data-testid={`enquiry-${f.k}`}
                       className="w-full border-b border-gold/25 bg-transparent py-2.5 font-sans text-sm text-warmwhite outline-none placeholder:text-softgrey/50 focus:border-gold" />
              ))}
              <textarea placeholder="Message (optional)" rows={3} value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="enquiry-message"
                        className="w-full border-b border-gold/25 bg-transparent py-2.5 font-sans text-sm text-warmwhite outline-none placeholder:text-softgrey/50 focus:border-gold" />
              <button type="submit" disabled={submitting} data-testid="enquiry-submit"
                      className="w-full bg-gold py-3.5 font-sans text-sm font-medium text-obsidian transition-all hover:brightness-110 disabled:opacity-60">
                {submitting ? "Sending…" : "Submit Enquiry"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
